import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { invalidateAdminInboxCounts } from '@/hooks/useAdminInboxCounts';
import { LEAD_SOURCES, type LeadSource } from '@/lib/follow-ups';

const LEAD_SOURCE_VALUES = LEAD_SOURCES.map((s) => s.value) as [LeadSource, ...LeadSource[]];

/**
 * The Enquiries admin surfaces two kinds of customer request:
 *  - 'enquiry'  — a quote request stored in the `enquiries` table.
 *  - 'callback' — a "request a callback" / contact-form submission stored in
 *                 the `callback_requests` table.
 * Both are normalised to this shape so they render in one list; `source`
 * tells mutations which table to act on.
 */
export type EnquirySource = 'enquiry' | 'callback';

export interface Enquiry {
  id: string;
  source: EnquirySource;
  customer_id?: string | null;
  converted_booking_id?: string | null;
  subject?: string | null;
  service_id: string | null;
  client_name: string;
  client_email: string;
  client_phone: string | null;
  address: string | null;
  frequency: string;
  premises_size: string;
  additional_info: string | null;
  status: string;
  created_at: string;
  service_name?: string;
  /** "Follow up on" date (yyyy-MM-dd), or null. */
  follow_up_on?: string | null;
  /** website | phone | referral | facebook | google | other (see src/lib/follow-ups). */
  lead_source?: string | null;
  /** Contact page messages only: what it's about (see src/lib/enquiry-types). */
  enquiry_type?: string | null;
}

interface ConvertEnquiryResult {
  booking_id?: string;
}

type ConvertEnquiryRpc = {
  rpc(
    name: 'convert_enquiry_to_booking',
    args: {
      _enquiry_id: string;
      _scheduled_date: string;
      _start_time: string;
      _end_time: string;
      _team_member_id: string | null;
    },
  ): Promise<{ data: ConvertEnquiryResult | string | null; error: unknown }>;
};

/** Normalises a `callback_requests` row into the shared Enquiry shape. */
function mapCallbackToEnquiry(row: {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  preferred_time: string | null;
  notes: string | null;
  status: string;
  created_at: string;
  follow_up_on?: string | null;
  lead_source?: string | null;
  enquiry_type?: string | null;
}): Enquiry {
  const isContactFormSubmission = row.preferred_time === 'Contact form submission';

  return {
    id: row.id,
    source: 'callback',
    service_id: null,
    client_name: row.name,
    client_email: row.email ?? '',
    client_phone: row.phone,
    address: null,
    frequency: '—',
    premises_size: '—',
    additional_info: row.notes,
    status: row.status,
    created_at: row.created_at,
    service_name: isContactFormSubmission ? 'Contact enquiry' : 'Callback request',
    follow_up_on: row.follow_up_on ?? null,
    lead_source: row.lead_source ?? 'website',
    enquiry_type: row.enquiry_type ?? null,
  };
}

/**
 * Fetches all customer requests for the org — quote enquiries plus callback
 * requests — normalised into one list and enriched with service names.
 * Mirrors the original fetchEnquiries waterfall: enquiries first, then services
 * lookup; callbacks have no service so they skip the lookup.
 */
export function useEnquiries(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['enquiries', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Enquiry[]> => {
      const [enquiriesResult, callbacksResult] = await Promise.all([
        supabase
          .from('enquiries')
          .select('*')
          .eq('organization_id', orgId!)
          .order('created_at', { ascending: false }),
        supabase
          .from('callback_requests')
          .select('*')
          .eq('organization_id', orgId!)
          .order('created_at', { ascending: false }),
      ]);

      if (enquiriesResult.error) throw enquiriesResult.error;
      if (callbacksResult.error) throw callbacksResult.error;

      const enquiriesData = enquiriesResult.data || [];

      const serviceIds = [
        ...new Set(enquiriesData.map((e) => e.service_id).filter(Boolean)),
      ];
      let serviceMap: Record<string, string> = {};

      if (serviceIds.length > 0) {
        const { data: servicesData } = await supabase
          .from('services')
          .select('id, name')
          .in('id', serviceIds);

        serviceMap = (servicesData || []).reduce(
          (acc, s) => ({ ...acc, [s.id]: s.name }),
          {},
        );
      }

      const enquiries: Enquiry[] = enquiriesData.map((e) => ({
        ...e,
        source: 'enquiry',
        service_name: e.service_id ? serviceMap[e.service_id] : 'Unknown Service',
      })) as Enquiry[];

      const callbacks: Enquiry[] = (callbacksResult.data || []).map(mapCallbackToEnquiry);

      return [...enquiries, ...callbacks].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    },
  });
}

export interface UpdateEnquiryStatusInput {
  enquiryId: string;
  orgId: string;
  newStatus: string;
  source: EnquirySource;
}

// Statuses that trigger the acceptance email — kept in sync with EnquiriesAdmin.
const ACCEPTED_STATUSES = new Set(['accepted', 'confirmed']);

/** Updates the status of an enquiry or callback request, fires acceptance email when warranted, and refreshes the list. */
export function useUpdateEnquiryStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateEnquiryStatusInput): Promise<void> => {
      const table = input.source === 'callback' ? 'callback_requests' : 'enquiries';
      const { error } = await supabase
        .from(table)
        .update({ status: input.newStatus })
        .eq('id', input.enquiryId)
        .eq('organization_id', input.orgId);

      if (error) throw error;

      if (input.source === 'enquiry' && ACCEPTED_STATUSES.has(input.newStatus)) {
        try {
          await supabase.functions.invoke('send-enquiry-accepted', {
            body: {
              recordId: input.enquiryId,
              source: 'enquiry',
              organizationId: input.orgId,
            },
          });
        } catch (notifyError) {
          console.error('Acceptance notification failed:', notifyError);
          // Don't fail the status update if the notification fails.
        }
      }
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['enquiries', input.orgId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

export interface DeleteEnquiryInput {
  enquiryId: string;
  orgId: string;
  source: EnquirySource;
}

/** Deletes an enquiry or callback request by ID and refreshes the list. */
export function useDeleteEnquiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteEnquiryInput): Promise<void> => {
      const table = input.source === 'callback' ? 'callback_requests' : 'enquiries';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', input.enquiryId)
        .eq('organization_id', input.orgId);

      if (error) throw error;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['enquiries', input.orgId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

export interface ConvertEnquiryInput {
  enquiryId: string;
  orgId: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
}

/** Calls the convert_enquiry_to_booking RPC and refreshes the enquiries list. */
export function useConvertEnquiry() {
  const queryClient = useQueryClient();

  return useMutation({
    // Resolves to the new job's id (the RPC returns it) so callers can, for
    // example, make it the first visit of a repeat clean.
    mutationFn: async (input: ConvertEnquiryInput): Promise<string | null> => {
      const { data, error } = await (
        supabase as unknown as ConvertEnquiryRpc
      ).rpc('convert_enquiry_to_booking', {
        _enquiry_id: input.enquiryId,
        _scheduled_date: input.scheduledDate,
        _start_time: input.startTime,
        _end_time: input.endTime,
        _team_member_id: null,
      });

      if (error) throw error;
      return typeof data === 'string' ? data : data?.booking_id ?? null;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['enquiries', input.orgId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

export interface UpdateEnquiryLeadInput {
  enquiryId: string;
  orgId: string;
  source: EnquirySource;
  /** yyyy-MM-dd, or null to clear. Omit to leave unchanged. */
  followUpOn?: string | null;
  leadSource?: LeadSource;
}

/** Sets an enquiry's follow-up date and/or lead source, then refreshes the list and the bell. */
export function useUpdateEnquiryLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateEnquiryLeadInput): Promise<void> => {
      const patch: { follow_up_on?: string | null; lead_source?: string } = {};
      if (input.followUpOn !== undefined) patch.follow_up_on = input.followUpOn;
      if (input.leadSource !== undefined) patch.lead_source = input.leadSource;
      if (Object.keys(patch).length === 0) return;
      const table = input.source === 'callback' ? 'callback_requests' : 'enquiries';
      const { error } = await supabase
        .from(table)
        .update(patch)
        .eq('id', input.enquiryId)
        .eq('organization_id', input.orgId);
      if (error) throw error;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['enquiries', input.orgId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-insights', input.orgId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

export const logEnquirySchema = z.object({
  client_name: z.string().trim().min(1, 'Add their name').max(120),
  client_email: z.string().trim().email('Enter a valid email address'),
  client_phone: z.string().trim().max(40).optional(),
  service_id: z.string().optional(),
  additional_info: z.string().trim().max(2000).optional(),
  lead_source: z.enum(LEAD_SOURCE_VALUES),
  follow_up_on: z.string().optional(),
});

export type LogEnquiryValues = z.infer<typeof logEnquirySchema>;

/**
 * Logs an enquiry that came in by phone, referral, social media etc. so it sits
 * in the same pipeline as website enquiries. Goes in as a new ('pending') lead.
 */
export function useLogEnquiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, values }: { orgId: string; values: LogEnquiryValues }): Promise<void> => {
      const { error } = await supabase.from('enquiries').insert({
        organization_id: orgId,
        client_name: values.client_name.trim(),
        client_email: values.client_email.trim().toLowerCase(),
        client_phone: values.client_phone?.trim() || null,
        service_id: values.service_id || null,
        additional_info: values.additional_info?.trim() || null,
        premises_size: 'Not specified',
        frequency: 'one-off',
        subject: 'New enquiry',
        status: 'pending',
        lead_source: values.lead_source,
        follow_up_on: values.follow_up_on || null,
      });
      if (error) throw error;
    },
    onSuccess: (_result, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['enquiries', orgId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-insights', orgId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}
